/**
 * Domain → market mapping (checked-in config).
 *
 * Each public storefront domain is pinned to one Spree market. This is the
 * storefront-side source of truth for domain ROUTING + PRESENTATION (which
 * domain, label, flag); it is needed synchronously at the edge (middleware), so
 * it lives in code rather than fetched from the API. The market DEFINITIONS
 * (currency / locale / tax / countries / which is default) are owned by the
 * Spree seed (backend db/seeds/tkf/markets.rb) — keep the two in sync.
 *
 * Market resolution: the storefront passes `country` to Spree. The Rest-of-World
 * domain uses the sentinel country `ww` (not a real ISO), which keeps every
 * entry's `country` unique (needed for canonical URLs + the region switcher).
 *
 * Spree does NOT resolve the sentinel — and does not fall back to the default
 * market on its own: `GET /markets/resolve` looks up a real `Spree::Country`
 * first, and `Market.for_country` returns nil for an unclaimed country. So
 * resolving `ww` (or any country no market claims) 404s. The storefront applies
 * the default-market fallback itself, in `lib/data/markets.ts#resolveMarket`.
 *
 * This module is import-safe in the edge runtime (no Node/Next APIs).
 */

export interface DomainMarket {
  /** Canonical host the entry is keyed on, e.g. "tongkatfitness.us". */
  host: string;
  /** ISO country Spree resolves the market from (lowercase); `ww` = RoW sentinel. */
  country: string;
  /** Locale code, e.g. "en". */
  locale: string;
  /** ISO 4217 currency code (display; the price currency comes from Spree). */
  currency: string;
  /** Human label for the region switcher. */
  label: string;
  /** Short store name for tight UI (store-suggestion bar), e.g. "UK". */
  shortLabel: string;
  /** Emoji shown in the region switcher (explicit, since the resolution country
   *  isn't always the display flag — e.g. EU uses `de` for resolution but 🇪🇺). */
  flag: string;
  /** The fallback domain (Rest of World). At most one entry. */
  default?: boolean;
  /**
   * `hreflang` value advertised for this domain's regional variant (see
   * `languageAlternatesForPath`). Explicit rather than derived from
   * country/locale: `.eu` spans many countries with no single ISO region, so
   * it uses the generic `en`. The default (RoW) domain omits this — it's
   * emitted as `x-default` instead.
   */
  hreflang?: string;
}

/**
 * The four Tongkat Fitness storefront domains. Mirrors the Spree market seed:
 * US is the default market (catch-all, USD), so `.com` (RoW) resolves to it.
 */
export const DOMAIN_MARKETS: DomainMarket[] = [
  {
    host: "tongkatfitness.us",
    country: "us",
    locale: "en",
    currency: "USD",
    label: "United States",
    shortLabel: "US",
    flag: "🇺🇸",
    hreflang: "en-US",
  },
  {
    host: "tongkatfitness.uk",
    country: "gb",
    locale: "en",
    currency: "GBP",
    label: "United Kingdom",
    shortLabel: "UK",
    flag: "🇬🇧",
    hreflang: "en-GB",
  },
  {
    host: "tongkatfitness.eu",
    country: "de",
    locale: "en",
    currency: "EUR",
    label: "Europe",
    shortLabel: "EU",
    flag: "🇪🇺",
    hreflang: "en",
  },
  {
    host: "tongkatfitness.com",
    country: "ww",
    locale: "en",
    currency: "USD",
    label: "Rest of World",
    shortLabel: "International",
    flag: "🌐",
    default: true,
  },
];

/** Environment-prefixed subdomains stripped before matching so stg/prod share one map. */
const ENV_PREFIXES = ["stg.", "staging.", "prd."];

/** All configured domain→market entries. */
export function getDomainMarkets(): DomainMarket[] {
  return DOMAIN_MARKETS;
}

/** The Rest-of-World / default entry. */
export function getDefaultDomain(): DomainMarket | undefined {
  return DOMAIN_MARKETS.find((m) => m.default) ?? DOMAIN_MARKETS[0];
}

/**
 * Normalize an incoming Host header for lookup: lowercase, drop any port, and
 * strip a leading environment prefix (`stg.`, `staging.`, `prd.`).
 */
export function normalizeHost(host: string): string {
  let h = host.toLowerCase().split(":")[0].trim();
  for (const prefix of ENV_PREFIXES) {
    if (h.startsWith(prefix)) {
      h = h.slice(prefix.length);
      break;
    }
  }
  return h;
}

/**
 * Resolve the pinned market for a request host. Returns the matching entry, or
 * `undefined` when the host isn't a configured storefront domain (e.g. localhost,
 * preview URLs) so the caller can fall back to legacy geo/cookie behaviour.
 */
export function resolveDomain(
  host: string | null | undefined,
): DomainMarket | undefined {
  if (!host) return undefined;
  const normalized = normalizeHost(host);
  return DOMAIN_MARKETS.find((m) => m.host === normalized);
}

/** The storefront domain the browser is on (client-side only; undefined on the server). */
export function currentDomain(): DomainMarket | undefined {
  return typeof window === "undefined"
    ? undefined
    : resolveDomain(window.location.host);
}

/**
 * The canonical origin (`https://<host>`) for a pinned country — used to build
 * per-domain canonical / OG URLs from a route's `params.country` WITHOUT reading
 * request headers, so pages stay statically prerenderable. Each entry's country
 * is unique (incl. the RoW `ww` sentinel). Returns `undefined` when unmapped, so
 * callers fall back to the configured store URL.
 */
export function domainOriginForCountry(country: string): string | undefined {
  const entry = DOMAIN_MARKETS.find((m) => m.country === country.toLowerCase());
  return entry ? `https://${entry.host}` : undefined;
}

/**
 * Build the `alternates.languages` (hreflang) map for a clean storefront path
 * across every pinned market domain, plus an `x-default` pointing at the
 * Rest-of-World (default) domain. Each public domain serves the same English
 * content at the same path, so without this Google treats the four origins as
 * competing cross-domain duplicates. The set is identical on every domain
 * variant (correct reciprocal hreflang) and self-referential.
 *
 * Uses the production apex origins (no env prefix) so the map is env-invariant
 * and the pages stay statically prerenderable — staging is Cloudflare-Access
 * gated and not indexed, so a prod-pointing hreflang there is harmless.
 */
export function languageAlternatesForPath(
  path: string,
): Record<string, string> {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const languages: Record<string, string> = {};
  for (const m of DOMAIN_MARKETS) {
    const url = `https://${m.host}${cleanPath}`;
    if (m.hreflang) languages[m.hreflang] = url;
    if (m.default) languages["x-default"] = url;
  }
  return languages;
}

/**
 * Apply the current host's environment prefix (`stg.` / `staging.`) to a target
 * canonical host, so a cross-domain region switch stays within the same
 * environment (e.g. stg.tongkatfitness.com → stg.tongkatfitness.uk). Production
 * apex hosts carry no prefix and pass through unchanged.
 */
export function applyEnvPrefix(
  targetHost: string,
  currentHost: string,
): string {
  const current = currentHost.toLowerCase().split(":")[0];
  for (const prefix of ENV_PREFIXES) {
    if (current.startsWith(prefix)) return `${prefix}${targetHost}`;
  }
  return targetHost;
}
